import { useEffect, useMemo, useRef, useState } from 'react';
import type { StructuralElementId } from '@obraplan/core/domain/shared/ids';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import { transladarContorno } from './pilar-livre-da-prancha';
import { closesPolygon, distance, pixelToMetres, resolvePoint, type Pixel } from './plan-tracer';
import type { TypologyDraft, WorkspaceAction } from './typology-draft';

/**
 * O pilar e a viga com contorno na prancha: o polígono que se traça e o elemento que se
 * arrasta (06/10/2026).
 *
 * - **Polígono**: clicando os vértices e fechando no primeiro, como o bloco. O retângulo por
 *   dois cantos continua no traçado da prancha — é o gesto que o pilar e a viga já tinham, e
 *   os trechos nas paredes saem dele como sempre;
 * - **Arrasto**: com a prancha parada, o elemento que tem contorno e nenhum trecho em parede
 *   anda inteiro, e a posição nova sobe como `UPDATE_ELEMENT`. O que tem trecho não anda: o
 *   desconto da alvenaria ficaria na parede de onde ele saiu.
 *
 * O estado mora aqui, e não dentro do traçado, pelo mesmo motivo dos hooks da fundação: o
 * traçado já carrega o de muitas ferramentas.
 */

export type FormatoDaPeca = 'RETANGULO' | 'POLIGONO';

/** O traço do polígono ainda não cadastrado, como a prancha o desenha. */
export interface TracoDaPeca {
  readonly vertices: readonly Pixel[];
  /** Fechado o contorno é polígono cheio; aberto, é uma linha andando até o cursor. */
  readonly fechado: boolean;
  /** O cursor, para o último lado ainda aberto. */
  readonly ao: Pixel | undefined;
}

/** O elemento na mão, arrastado: o pixel por onde ele passa até o botão subir. */
export interface ArrastoDaPeca {
  readonly id: StructuralElementId;
  readonly origem: Pixel;
  readonly atual: Pixel;
  /** Passou da folga do clique? Antes disso é clique, e clicar seleciona. */
  readonly moveu: boolean;
}

/** A folga do clique, em pixels **de tela**, como a do encaixe do contorno. */
const FOLGA_DO_ARRASTO = 4;
const FOLGA_VERTICE = 10;
const FOLGA_ORTOGONAL = 8;

/** Quanto tempo depois de soltar um arrasto o clique que veio com ele ainda é dele. */
const JANELA_DO_CLIQUE_MS = 100;

export function usePecaLivreNaPrancha({
  draft,
  escala,
  zoom,
  ativo,
  parado,
  vertices: verticesExistentes,
  dispatch,
}: {
  draft: TypologyDraft;
  escala: number | undefined;
  zoom: number;
  /** A ferramenta "Pilar e viga" está ligada? */
  ativo: boolean;
  /** A prancha está parada — sem ferramenta nenhuma ligada? */
  parado: boolean;
  /** Os vértices dos ambientes, em pixel: o ponto novo se encaixa neles. */
  vertices: readonly Pixel[];
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const [formato, setFormato] = useState<FormatoDaPeca>('RETANGULO');
  /** Os vértices do polígono, em pixel da prancha. */
  const [pontos, setPontos] = useState<readonly Pixel[]>([]);
  const [fechado, setFechado] = useState(false);
  const [cursor, setCursor] = useState<Pixel | undefined>(undefined);
  const [arrastando, setArrastando] = useState<ArrastoDaPeca | undefined>(undefined);
  /** Quando o último arrasto terminou — o clique que vem junto com ele não é de ninguém. */
  const cliqueDoArrasto = useRef<number | undefined>(undefined);

  // Trocar de tipologia ou de ferramenta descarta o que estava na mão: o ponto marcado é do
  // desenho e da ferramenta que saíram da mesa.
  const emDesenho = draft.unitTypeId;
  useEffect(() => {
    setPontos([]);
    setFechado(false);
    setCursor(undefined);
    setArrastando(undefined);
  }, [emDesenho, ativo]);

  /** O contorno do polígono de agora, em metros — ausente enquanto o traço não fechou. */
  const contorno = useMemo((): readonly Point[] | undefined => {
    if (escala === undefined || formato !== 'POLIGONO' || !fechado) return undefined;
    return pontos.map((ponto) => pixelToMetres(ponto, escala));
  }, [escala, formato, pontos, fechado]);

  /** Os elementos que andam: com contorno e sem trecho em parede. */
  const arrastaveis = useMemo(() => {
    const comTrecho = new Set(draft.elementSegments.map((trecho) => String(trecho.elementId)));
    return new Map(
      draft.elements
        .filter(
          (element) =>
            element.deletedAt === undefined &&
            element.outline !== undefined &&
            !comTrecho.has(String(element.id)),
        )
        .map((element) => [String(element.id), element.outline!] as const),
    );
  }, [draft.elements, draft.elementSegments]);

  /** Onde o vértice cai: encaixado em um vértice conhecido, ou no esquadro do anterior. */
  function verticeDaPeca(bruto: Pixel, anterior?: Pixel): Pixel {
    return resolvePoint(bruto, {
      vertices: [...verticesExistentes, ...pontos],
      vertexTolerance: FOLGA_VERTICE / zoom,
      ...(anterior === undefined
        ? {}
        : { previous: anterior, orthogonalTolerance: FOLGA_ORTOGONAL / zoom }),
    });
  }

  /** O clique no vazio da prancha, com o polígono ligado. */
  function clicarNaPrancha(bruto: Pixel): void {
    if (!ativo || formato !== 'POLIGONO' || escala === undefined) return;
    if (fechado) {
      setFechado(false);
      setPontos([verticeDaPeca(bruto)]);
      return;
    }
    if (closesPolygon(bruto, pontos, FOLGA_VERTICE / zoom)) {
      setFechado(true);
      return;
    }
    setPontos((atual) => [...atual, verticeDaPeca(bruto, atual[atual.length - 1])]);
  }

  /** O botão desceu num elemento com contorno: se andar, ele vai junto. */
  function apertarNaPeca(alvo: Element, ponto: Pixel): boolean {
    if (!parado || escala === undefined) return false;
    const id = alvo.closest('[data-peca-contorno]')?.getAttribute('data-peca');
    if (id === null || id === undefined || !arrastaveis.has(id)) return false;
    setArrastando({
      id: id as StructuralElementId,
      origem: ponto,
      atual: ponto,
      moveu: false,
    });
    return true;
  }

  /** O mouse andou: o elemento na mão e o segmento vivo do polígono. */
  function acompanhar(ponto: Pixel): boolean {
    if (arrastando !== undefined) {
      setArrastando({
        ...arrastando,
        atual: ponto,
        moveu: arrastando.moveu || distance(arrastando.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
      });
      return true;
    }
    // O segmento vivo só existe com traço pela metade; fora disso o cursor não custa render.
    if (ativo && formato === 'POLIGONO' && pontos.length > 0 && !fechado) setCursor(ponto);
    return false;
  }

  /** O botão subiu: grava o movimento do elemento arrastado. */
  function soltar(ponto: Pixel): boolean {
    const corrente = arrastando;
    if (corrente === undefined) return false;
    setArrastando(undefined);
    if (escala === undefined) return true;
    // Soltar onde apertou é clique, e clicar seleciona: o elemento não sai do lugar.
    if (!corrente.moveu && distance(corrente.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) {
      return true;
    }
    cliqueDoArrasto.current = Date.now();
    const outline = arrastaveis.get(String(corrente.id));
    if (outline === undefined) return true;
    dispatch({
      type: 'UPDATE_ELEMENT',
      elementId: corrente.id,
      patch: {
        outline: transladarContorno(
          outline,
          (ponto.x - corrente.origem.x) * escala,
          (ponto.y - corrente.origem.y) * escala,
        ),
      },
    });
    return true;
  }

  /**
   * O clique que o fim do arrasto dispara é do arrasto; consome-o, e só uma vez.
   *
   * A janela existe para que um arrasto cujo clique nunca veio — soltar fora da prancha — não
   * engula o clique seguinte, que é de outra pessoa.
   */
  function foiCliqueDoArrasto(): boolean {
    const desde = cliqueDoArrasto.current;
    cliqueDoArrasto.current = undefined;
    return desde !== undefined && Date.now() - desde < JANELA_DO_CLIQUE_MS;
  }

  function refazer(): void {
    setPontos([]);
    setFechado(false);
    setCursor(undefined);
  }

  /** O traço do polígono como o desenho o mostra: andando, ou fechado. */
  const traco: TracoDaPeca | undefined =
    ativo && formato === 'POLIGONO' && pontos.length > 0
      ? { vertices: pontos, fechado, ao: fechado ? undefined : cursor }
      : undefined;

  return {
    formato,
    escolherFormato: (novo: FormatoDaPeca) => {
      setFormato(novo);
      refazer();
    },
    /** Quantos vértices do polígono já foram clicados. */
    vertices: pontos.length,
    fechado,
    contorno,
    traco,
    fecharContorno: () => {
      if (formato === 'POLIGONO' && pontos.length >= 3) setFechado(true);
    },
    desfazerPonto: () => {
      if (fechado) setFechado(false);
      else setPontos((atual) => atual.slice(0, -1));
    },
    refazer,
    arrastando,
    clicarNaPrancha,
    apertarNaPeca,
    acompanhar,
    soltar,
    cancelarArrasto: () => setArrastando(undefined),
    foiCliqueDoArrasto,
  };
}

export type PecaLivreNaPrancha = ReturnType<typeof usePecaLivreNaPrancha>;
